// Tarjeta de una línea de venta cerrada (comprobante, Mis ventas y Ventas): dos renglones fijos,
// nombre y precio (o la promo en verde) arriba; categoría, SKU con su IMEI y forma de pago abajo.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { shownDiscountCode } from '@/shared/patterns/SalePromotion';
import type { SaleReceiptRow } from '@/shared/lib/format/saleReceipt';
import { LinePromotionOutlet } from './SaleSlotOutlets';
import type { SaleSlots, SlotSale } from './saleSlots';

function money(cents: number): string {
    return formatMXN(cents / 100);
}

/** La línea lleva promo: un regalo, una promo de venta o un código. */
function hasPromotion(line: SaleLine): boolean {
    return line.role === 'GIFT' || !!line.promotionId || !!shownDiscountCode(line);
}

/** La línea mixta dice cuánto fue a crédito y cuánto de contado; la pura, solo su forma de pago. */
function PaymentCell({ row }: { row: SaleReceiptRow }) {
    const { split } = row;
    if (split && split.financedCents > 0 && split.cashCents > 0) {
        return (
            <span className="shrink-0 tabular-nums" title={row.paymentLabel ?? undefined}>
                <span>Financiado {money(split.financedCents)}</span>
                {' · '}
                <span>Contado {money(split.cashCents)}</span>
            </span>
        );
    }
    return <span className="shrink-0">{row.paymentLabel}</span>;
}

export function SaleLineCard({
    row,
    sale,
    linePromotion,
    giftName,
    compact = false,
    className,
}: {
    row: SaleReceiptRow;
    sale: SlotSale;
    linePromotion: SaleSlots['linePromotion'];
    giftName?: SaleSlots['giftName'];
    /** Letra más chica para la ficha lateral. */
    compact?: boolean;
    className?: string;
}) {
    const main = compact ? 'text-[11.5px]' : 'text-[12px]';
    const minor = compact ? 'text-[10.5px]' : 'text-[11px]';
    const promoted = linePromotion !== undefined && hasPromotion(row.line);
    return (
        <li className={cn('rounded-md border border-border px-2.5 py-1.5', className)}>
            <div className={cn('flex items-baseline justify-between gap-2', main)}>
                <span className="min-w-0 truncate font-medium" title={row.name}>
                    {giftName && row.line.role === 'GIFT'
                        ? giftName(row.line, sale, row.name)
                        : row.name}
                </span>
                {promoted ? (
                    <LinePromotionOutlet
                        slot={linePromotion}
                        line={row.line}
                        sale={sale}
                        className="min-w-0 max-w-[70%] shrink-0"
                    />
                ) : (
                    <span className="shrink-0 tabular-nums">{money(row.netCents)}</span>
                )}
            </div>
            <div
                className={cn(
                    'flex items-baseline justify-between gap-2 text-muted-foreground',
                    minor,
                )}
            >
                <span className="min-w-0 truncate" title={row.detail}>
                    {row.categoryLabel && <span>{row.categoryLabel} · </span>}
                    <span className="font-mono">{row.detail}</span>
                </span>
                <PaymentCell row={row} />
            </div>
            {row.identifiers.map((group) => (
                <div key={group.sku} className={cn('flex flex-wrap gap-x-2', minor)}>
                    <span className="font-mono text-muted-foreground">{group.sku}</span>
                    {group.values.map((value) => (
                        <span key={value} className="font-mono">
                            {value}
                        </span>
                    ))}
                </div>
            ))}
        </li>
    );
}
